/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { useRelayEnvironment, fetchQuery } from 'react-relay';
import { useField } from 'formik';
import PropTypes from 'prop-types';

// Local Definitions

import { SelectInput } from "../..";

/**
 * This is the component description.
 */
const QuerySelect = ({
  id,
  className: userClassName,
  style,
  name,
  label,
  labelColor,
  optional,
  query,
  variables,
  optionsKeyMap,
  description,
  disabled,
  color,
  loadingOption,
  defaultOption,
  validate
  // ...otherProps
}) => {
  const [,, helpers] = useField({
    name,
    validate
  });
  const {
    setError
  } = helpers;
  const environment = useRelayEnvironment();
  const [isFetching, setIsFetching] = useState(false);
  const [options, setOptions] = useState([]);
  const {
    graphql,
    accessor
  } = query;
  const getOptions = () => {
    if (isFetching) return;
    fetchQuery(environment, graphql, variables).subscribe({
      start: () => {
        setIsFetching(true);
        setOptions([loadingOption]);
      },
      complete: () => {
        setIsFetching(false);
      },
      error: fetchError => {
        setIsFetching(false);
        if (setError) setError(fetchError.message);
      },
      next: data => {
        setOptions([defaultOption, ...data[accessor].edges.map(({
          node
        }) => ({
          value: node[optionsKeyMap.value],
          label: optionsKeyMap.getLabel(node)
        }))]);
      }
    });
  };
  useEffect(() => {
    getOptions();
  }, [variables]);
  return /*#__PURE__*/React.createElement(SelectInput, {
    id: id,
    className: userClassName,
    style: style,
    name: name,
    label: label,
    optional: optional,
    color: color,
    labelColor: labelColor,
    description: description,
    disabled: isFetching || disabled,
    options: options,
    isLoading: isFetching
  });
};
QuerySelect.propTypes = {
  /**
   * The HTML id for this element
   */
  id: PropTypes.string,
  /**
   * The HTML class names for this element
   */
  className: PropTypes.string,
  /**
   * The React-written, css properties for this element.
   */
  style: PropTypes.objectOf(PropTypes.string),
  /**
   * The name of the custom select input
   */
  name: PropTypes.string,
  /**
   * The label of the custom select input
   */
  label: PropTypes.string,
  /**
   * The custom select input description
   */
  description: PropTypes.string,
  /**
   * Whether the input should be disabled
   */
  disabled: PropTypes.bool,
  /**
   * The base color of the custom select input
   */
  color: PropTypes.string,
  /**
   * The label color of the custom select input
   */
  labelColor: PropTypes.string,
  /**
   * The graphql query to fetch the options and the accessor to destructure the results from
   */
  query: PropTypes.shape({
    accessor: PropTypes.string,
    graphql: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired
  }),
  /**
   *  The variables that might be required to be used in the query to fetch
   *  select options.
   */
  variables: PropTypes.objectOf(PropTypes.string),
  /**
   *  The select option keys to be used to map the data to the select options.
   * i.e `{ value: 'id', label: 'name' }`
   */
  optionsKeyMap: PropTypes.shape({
    value: PropTypes.string.isRequired,
    getLabel: PropTypes.func.isRequired
  }).isRequired,
  /**
   * The default select option for the query select
   */
  defaultOption: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired
  }),
  /**
   * The option to dipslayed when the select options are being fetched
   */
  loadingOption: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired
  }),
  /**
   * The query select field validator function
   */
  validate: PropTypes.func,
  /*
  * Whether the input is optional or not
  */
  optional: PropTypes.bool
};
QuerySelect.defaultProps = {
  disabled: false,
  color: 'paragraph',
  defaultOption: {
    value: '',
    label: 'Select an option',
    disabled: true
  },
  loadingOption: {
    value: '',
    label: 'Fetching Options',
    disabled: true
  }
};
export default QuerySelect;